import React from 'react';
import { useState } from 'react';
import { useAppStore } from '../store/useAppStore';
import { deductCredits, checkUserCredits } from '../utils/creditManager';
import { ModelConfig } from './useModels';

interface GenerationResult {
  success: boolean;
  assets?: any[];
  error?: string;
}

/** Edge queue polling can run up to ~3m; video models may run longer. */
function falGenerationTimeoutMs(model: ModelConfig, isBackground: boolean): number {
  if (isBackground) return 120_000;
  if (model.fal_client_mode === 'queue') return 280_000;
  if (model.output_type === 'video') return 600_000;
  return 360_000;
}

async function fetchFalGeneration(
  body: object,
  timeoutMs: number
): Promise<Response> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fal-generation`;
  const controller = new AbortController();
  const tid = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(tid);
  }
}

export const useFalGeneration = (model: ModelConfig | null, apiKeyId?: string, pageAppId?: string) => {
  const { session, openInsufficientCreditsModalWithAmount } = useAppStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [generatedAssets, setGeneratedAssets] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [creditsChecked, setCreditsChecked] = useState(false);
  const [hasSufficientCredits, setHasSufficientCredits] = useState(false);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);

  // Check credits when component mounts or model changes
  React.useEffect(() => {
    const checkCredits = async () => {
      if (!session || !model) {
        setCreditsChecked(false);
        setHasSufficientCredits(false);
        return;
      }

      console.log('💰 [CREDITS] Checking user credits on component mount');
      
      const creditCheck = await checkUserCredits(session, model.credit_cost);
      
      setCurrentCredits(creditCheck.currentCredits || null);
      if (creditCheck.success) {
        setHasSufficientCredits(creditCheck.hasCredits);
        setCreditsChecked(true);
        
        if (!creditCheck.hasCredits) {
          // Only open modal if credits are actually insufficient
          console.log('❌ [CREDITS] Insufficient credits detected, showing modal');
          openInsufficientCreditsModalWithAmount(model.credit_cost);
        } else {
          console.log('✅ [CREDITS] User has sufficient credits');
        }
      } else {
        console.error('❌ [CREDITS] Credit check failed:', creditCheck.error);
        setCreditsChecked(true);
        setHasSufficientCredits(false);
        // Don't show modal on check failure, let user try to generate
      }
    };

    checkCredits();
  }, [session, model, openInsufficientCreditsModalWithAmount]);

  const generateContent = async (formData: Record<string, any>): Promise<GenerationResult> => {
    const finalAppId = String(pageAppId || formData.app_id || '').trim();
    console.log('🆔 [FAL-GEN] App ID (page or form):', finalAppId);

    if (!session || !model) {
      return { success: false, error: 'Session or model not available' };
    }

    if (!finalAppId) {
      return {
        success: false,
        error: 'App ID is missing — set it in Page Settings for this page (or Pro Form / ProMax model).',
      };
    }

    try {
      setIsGenerating(true);
      setShowResults(false);
      setGenerationComplete(false);
      setError(null);
      setGeneratedAssets([]);
      setGenerationProgress('Preparing generation...');
      console.log('🚀 [FAL-GEN] Starting generation with model:', model.name);

      // Step 1: Process form data
      console.log('📋 [FAL-GEN] Processing form data:', formData);
      const processedData = await processFormData(formData, model);
      console.log('✅ [FAL-GEN] Processed form data:', processedData);

      // Step 2: Check if this is a background processing model
      if (model.is_long_running) {
        console.log('⏳ [FAL-GEN] Background processing model detected');
        const result = await handleBackgroundGeneration(model, processedData, session, finalAppId);
        return result;
      } else {
        console.log('⚡ [FAL-GEN] Fast processing model detected');
        const result = await handleFastGeneration(model, processedData, session, finalAppId);
        return result;
      }

    } catch (error) {
      console.error('💥 [FAL-GEN] Generation error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'An unexpected generation error occurred' };
    } finally {
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  const processFormData = async (formData: Record<string, any>, model: ModelConfig): Promise<Record<string, any>> => {
    const processedData: Record<string, any> = {};

    // Process each field in the input schema
    for (const field of model.input_schema || []) {
      const fieldKey = field.key || field.variable_name || field.id;
      const fieldValue = formData[fieldKey];

      console.log(`🔄 [FAL-FORM] Processing field "${fieldKey}":`, fieldValue);

      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        if (field.type === 'file') {
          // Handle file fields - extract public_url
          if (field.multiple && Array.isArray(fieldValue)) {
            // Always send as array when multiple is enabled
            const urls = fieldValue.map(asset => asset.public_url).filter(Boolean);
            processedData[fieldKey] = urls;
            console.log(`📁 [FAL-FORM] Multiple files processed for "${fieldKey}":`, urls);
          } else if (fieldValue && typeof fieldValue === 'object' && fieldValue.public_url) {
            processedData[fieldKey] = fieldValue.public_url;
            console.log(`📁 [FAL-FORM] Single file processed for "${fieldKey}":`, fieldValue.public_url);
          }
        } else {
          // Handle regular fields
          processedData[fieldKey] = fieldValue;
          console.log(`📝 [FAL-FORM] Regular field processed for "${fieldKey}":`, fieldValue);
        }

        // Handle additional variables
        if (field.additional_variables && field.additional_variables.length > 0) {
          console.log(`🔗 [FAL-FORM] Processing additional variables for "${fieldKey}":`, field.additional_variables);
          
          field.additional_variables.forEach((additionalVar: string) => {
            if (additionalVar && additionalVar.trim()) {
              const varType = field.additional_variable_types?.[additionalVar] || 'string';
              
              if (varType === 'list') {
                processedData[additionalVar] = Array.isArray(processedData[fieldKey]) 
                  ? processedData[fieldKey] 
                  : [processedData[fieldKey]];
                console.log(`📋 [FAL-FORM] Additional variable "${additionalVar}" set as list:`, processedData[additionalVar]);
              } else {
                processedData[additionalVar] = processedData[fieldKey];
                console.log(`📋 [FAL-FORM] Additional variable "${additionalVar}" set as string:`, processedData[additionalVar]);
              }
            }
          });
        }
      }
    }

    return processedData;
  };

  const handleFastGeneration = async (
    model: ModelConfig, 
    processedData: Record<string, any>, 
    session: any,
    appId: string
  ) => {
    try {
      setGenerationProgress('Starting AI generation...');
      console.log('⚡ [FAL-FAST] Starting fast generation');

      // Call the new FAL generation Edge Function
      console.log('📤 [FAL-FAST] Calling FAL generation Edge Function');
      
      const response = await fetchFalGeneration(
        {
          endpoint: model.fal_endpoint,
          input: processedData,
          api_key_id: apiKeyId,
          user_id: session.hy_user_id,
          app_id: appId,
          model_config: {
            name: model.name,
            credit_cost: model.credit_cost,
            output_type: model.output_type,
            output_mapping: model.output_mapping,
            is_long_running: model.is_long_running,
            fal_stream_node_id: model.fal_stream_node_id,
            fal_polling_node_name: model.fal_polling_node_name,
            fal_client_mode: model.fal_client_mode,
            fal_queue_webhook_url: model.fal_queue_webhook_url ?? null,
          },
          generation_metadata: {
            generation_prompt: processedData.prompt || JSON.stringify(processedData),
            generation_params: processedData,
          },
        },
        falGenerationTimeoutMs(model, false)
      );

      const result = await response.json().catch(() => ({} as Record<string, unknown>));
      
      if (!response.ok) {
        console.error('❌ [FAL-FAST] Edge Function error:', result);
        const code = result.code as string | undefined;
        const msg = (result.message || result.error) as string | undefined;
        if (response.status === 504 || code === 'IDLE_TIMEOUT') {
          return {
            success: false,
            error:
              msg ||
              'The platform closed the connection after ~150s while waiting for FAL. Deploy the latest fal-generation (queue jobs finish in the background) or check Assets / Processing for output.',
          };
        }
        return { success: false, error: msg || 'Generation failed' };
      }
      
      if (!result.success) {
        console.error('❌ [FAL-FAST] Generation failed:', result.error);
        return { success: false, error: (result.error as string) || 'Generation failed' };
      }

      const isMockSession = session.original_session_id === 'mock';

      if (result.deferred_queue && result.asset_id) {
        console.log('⏳ [FAL-FAST] Deferred queue job registered:', result.asset_id);
        if (!isMockSession) {
          setGenerationProgress('Recording credits…');
          const creditResult = await deductCredits(session, model.credit_cost);
          if (!creditResult.success) {
            console.error('❌ [CREDITS] Credit deduction failed after queue submit:', creditResult.error);
          } else {
            console.log('✅ [CREDITS] Credits deducted for deferred queue job');
          }
        }
        const placeholderAsset = {
          id: result.asset_id as string,
          status: 'processing' as const,
          public_url: '',
          filename: 'processing',
          message:
            (result.message as string) ||
            'Placeholder saved in your library. When FAL completes, our webhook updates this asset automatically.',
          fal_request_id: result.request_id as string | undefined,
        };
        setGeneratedAssets([placeholderAsset]);
        window.dispatchEvent(new CustomEvent('refreshAssetGallery'));
        setShowResults(true);
        setGenerationComplete(true);
        return { success: true, assets: [placeholderAsset] };
      }

      console.log('✅ [FAL-FAST] Generation completed successfully');
      const savedAssets = (result.assets as any[]) || [];

      // Step 5: Deduct credits ONLY after successful upload (skip for mock sessions)
      if (!isMockSession && savedAssets.length > 0) {
        console.log('💳 [FAL-FAST] Deducting credits after successful upload:', model.credit_cost);
        setGenerationProgress('Finalizing transaction...');
        
        const creditResult = await deductCredits(session, model.credit_cost);
        
        if (!creditResult.success) {
          console.error('❌ [CREDITS] Credit deduction failed after upload:', creditResult.error);
          // Note: Assets are already uploaded, but credit deduction failed
          // In a production system, you might want to handle this scenario differently
          // For now, we'll log the error but still show the results
        } else {
          console.log('✅ [FAL-FAST] Credits deducted successfully after upload');
        }
      } else if (isMockSession) {
        console.log('🧪 [FAL-FAST] Mock session detected - skipping credit deduction');
      } else {
        console.log('⚠️ [FAL-FAST] No assets saved - skipping credit deduction');
      }
      setGeneratedAssets(savedAssets);
      console.log('🎉 [FAL-FAST] Fast generation completed with', savedAssets.length, 'assets');

      // Trigger refresh event for asset gallery
      window.dispatchEvent(new CustomEvent('refreshAssetGallery'));

      
      // Set results to show in form
      setShowResults(true);
      setGenerationComplete(true);
      return { success: true, assets: savedAssets };

    } catch (error) {
      console.error('💥 [FAL-FAST] Fast generation error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error:
            'Generation timed out — the server took too long. For long videos try a background model, or retry. Check the Processing page if a job was created.',
        };
      }
      return { success: false, error: error instanceof Error ? error.message : 'Fast generation failed' };
    }
  };

  const handleBackgroundGeneration = async (
    model: ModelConfig, 
    processedData: Record<string, any>, 
    session: any,
    appId: string
  ) => {
    try {
      // Step 1: Credit deduction for background generation (must happen upfront)
      const isMockSession = session.original_session_id === 'mock';
      
      if (!isMockSession) {
        console.log('💳 [FAL-BG] Deducting credits upfront for background generation:', model.credit_cost);
        const creditResult = await deductCredits(session, model.credit_cost);
        
        if (!creditResult.success) {
          if (creditResult.error === 'Insufficient credits') {
            console.log('❌ [CREDITS] Insufficient credits during background generation, showing modal');
            openInsufficientCreditsModalWithAmount(model.credit_cost);
            return { success: false, error: 'Insufficient credits' };
          }
          return { success: false, error: creditResult.error || 'Credit deduction failed' };
        }
        
        console.log('✅ [FAL-BG] Credits deducted successfully for background generation');
      } else {
        console.log('🧪 [FAL-BG] Mock session detected - skipping credit deduction');
      }

      setGenerationProgress('Starting background generation...');
      console.log('⏳ [FAL-BG] Starting background generation');

      // Call the new FAL generation Edge Function for background processing
      console.log('📤 [FAL-BG] Calling FAL generation Edge Function for background processing');
      
      const response = await fetchFalGeneration(
        {
          endpoint: model.fal_endpoint,
          input: processedData,
          api_key_id: apiKeyId,
          user_id: session.hy_user_id,
          app_id: appId,
          model_config: {
            name: model.name,
            credit_cost: model.credit_cost,
            output_type: model.output_type,
            output_mapping: model.output_mapping,
            is_long_running: model.is_long_running,
            fal_stream_node_id: model.fal_stream_node_id,
            fal_polling_node_name: model.fal_polling_node_name,
            fal_client_mode: model.fal_client_mode,
            fal_queue_webhook_url: model.fal_queue_webhook_url ?? null,
          },
          generation_metadata: {
            generation_prompt: processedData.prompt || JSON.stringify(processedData),
            generation_params: processedData,
          },
        },
        falGenerationTimeoutMs(model, true)
      );

      const result = await response.json();
      
      if (!response.ok) {
        console.error('❌ [FAL-BG] Edge Function error:', result);
        return { success: false, error: result.error || 'Background generation failed' };
      }
      
      if (!result.success) {
        console.error('❌ [FAL-BG] Background generation failed:', result.error);
        return { success: false, error: result.error || 'Background generation failed' };
      }

      console.log('✅ [FAL-BG] Background generation started successfully');
      setGenerationProgress('Generation started! You will be notified when ready.');

      return { 
        success: true, 
        assets: [{
          id: result.asset_id,
          status: 'processing',
          message: 'Generation started in background'
        }]
      };

    } catch (error) {
      console.error('💥 [FAL-BG] Background generation error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Request timed out while starting background generation. Check your connection and try again.',
        };
      }
      return { success: false, error: error instanceof Error ? error.message : 'Background generation failed' };
    }
  };

  return {
    isGenerating,
    generationProgress,
    generatedAssets,
    showResults,
    generationComplete,
    error,
    generateContent,
    creditsChecked, // Indicates if the credit check has been performed
    hasSufficientCredits, // Indicates if the user has enough credits
    currentCredits // The actual current credit balance
  };
};